import * as React from 'react'
import { View } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import SegmentedControl from '@components/segmentedControl/SegmentedControl'
import Select from '@components/select/Select'
import DocumentCard from './DocumentCard'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as ControleStore from '@store/controle/controle'
import * as AuthStore from '@store/auth/auth'

import useBreakpoint from '@utils/useBreakpoints'

import { EtatDesLieuxTypeEnum, AuthScope } from '@api/enum'

import { findTenant, findManifestation, getEDLTypeTenant } from '@screens/edl/utils'
import { isManifestationIN, isManifestationOUT } from '@screens/edl/list/useEDL'

import { createUUID } from '@utils/uuid'
import { isManifestationValidForCQ } from '@screens/controle/form/utils'

interface Props {
  documents: Documents
  id: string
  isManifestation: boolean
  edlList: EtatDesLieuxResponse[]
}

const DocumentsScreen = ({ documents, id, isManifestation, edlList }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const newId = createUUID()

  const [, currentBreakpoint] = useBreakpoint()
  const isPhone = currentBreakpoint === 'phone'

  const scopes = useReducer(AuthStore.store, (s) => s.scopes)

  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const tenants = useReducer(SiteStore.store, (s) => s.tenants)
  const manifestationDetails = React.useMemo(() => findManifestation(manifestations, id), [manifestations, id])
  const tenantDetails = React.useMemo(() => findTenant(tenants, id), [id, manifestations])

  const canCreateEntryEDL =
    !!manifestationDetails && manifestationDetails.actif && scopes.includes(AuthScope.EdlEntreeCreation)
  const canCreateExitEDL =
    !!manifestationDetails && manifestationDetails.actif && scopes.includes(AuthScope.EdlSortieCreation)

  const canManifEntryEdl =
    !!manifestationDetails && manifestationDetails.actif && canCreateEntryEDL && isManifestationIN(manifestationDetails)

  const canManifExitEdl =
    !!manifestationDetails && manifestationDetails.actif && canCreateExitEDL && isManifestationOUT(manifestationDetails)

  const canTenantEntryEdl = React.useMemo(
    () => !!tenantDetails && getEDLTypeTenant(tenantDetails) === EtatDesLieuxTypeEnum.ENTREE,
    [tenantDetails]
  )
  const canTenantExitEdl = React.useMemo(
    () => !!tenantDetails && getEDLTypeTenant(tenantDetails) === EtatDesLieuxTypeEnum.SORTIE,
    [tenantDetails]
  )

  const canCreateChecklist =
    !!manifestationDetails && manifestationDetails.actif && scopes.includes(AuthScope.CheckListSiteCreation)

  const canCreateCQ = !!manifestationDetails && isManifestationValidForCQ(manifestationDetails, new Date())

  const entryEdlNumber = edlList.filter((e) => e.type === EtatDesLieuxTypeEnum.ENTREE).length
  const exitEdlNumber = edlList.filter((e) => e.type === EtatDesLieuxTypeEnum.SORTIE).length

  const tabs: { label: string; value: keyof Documents }[] = [
    { label: i18n.t('screens.documents.edl'), value: 'edl' },
    { label: i18n.t('screens.documents.checklist'), value: 'checklist' },
    { label: i18n.t('components.documentsSection.quality'), value: 'quality' },
  ]
  const [tab, setTab] = React.useState(tabs[0])

  React.useEffect(() => {
    if (!documents) {
      navigation.push('/')
    }
  }, [])

  if (!documents) {
    return null
  }

  const renderButton = (name: string, onClick: () => void) => (
    <SelectCard>
      <Select label={name} onClick={onClick} left icon="add" />
    </SelectCard>
  )

  const renderEDLDocuments = () => {
    return (
      <>
        {(canManifEntryEdl || canTenantEntryEdl) && (
          <SelectCard>
            <Select
              label={i18n.t('components.documentsSection.startEntryEDL')}
              onClick={() =>
                navigation.push(`/edl/spaces/${newId}?type=ENTREE&manifestationId=${id}&origin=manifestation`)
              }
              left
              icon="add"
            />
          </SelectCard>
        )}
        {(canManifExitEdl || canTenantExitEdl) && (
          <SelectCard>
            <Select
              label={i18n.t('components.documentsSection.startExitEDL')}
              onClick={() =>
                navigation.push(`/edl/spaces/${newId}?type=SORTIE&manifestationId=${id}&origin=manifestation`)
              }
              left
              icon="add"
            />
          </SelectCard>
        )}
        {(entryEdlNumber > 0 || exitEdlNumber > 0) && (
          <SelectCard>
            <Select
              label={i18n.t('components.documentsSection.edlList')}
              onClick={() => navigation.push(`/edl?manifestationId=${id}&list=finished`)}
              left
              icon="document"
            />
          </SelectCard>
        )}
      </>
    )
  }

  const renderChecklistDocuments = () => {
    return documents.checklist ? (
      <DocumentCard document={documents.checklist} name={i18n.t('screens.documents.checklist')} />
    ) : (
      canCreateChecklist &&
        renderButton(i18n.t('screens.documents.checklistButton'), () => navigation.push('/checklist'))
    )
  }

  const renderQualityDocuments = () => {
    return (
      <>
        {documents.quality.length > 0 && (
          <SelectCard>
            <Select
              label={i18n.t('screens.documents.quality', { count: documents.quality.length })}
              onClick={() => navigation.push(`/controles/history/list?manifestationId=${id}`)}
              left
              icon="document"
            />
          </SelectCard>
        )}

        {canCreateCQ &&
          renderButton(i18n.t('screens.documents.qualityButton'), () => {
            ControleStore.actions.resetForm()
            navigation.push(`/controles/form/presta?manifestationId=${id}`)
          })}
      </>
    )
  }

  return (
    <Root header={i18n.t('screens.documents.title')} onLeft={navigation.goBack}>
      {!isPhone && <Header title={i18n.t('screens.documents.title')} />}
      <MainContainer>
        <SegmentedContainer>
          <SegmentedControl data={tabs} value={tab} onItemSelected={setTab} />
        </SegmentedContainer>
        <DocumentList>
          {tab.value === 'edl'
            ? renderEDLDocuments()
            : tab.value === 'checklist'
            ? renderChecklistDocuments()
            : renderQualityDocuments()}
        </DocumentList>
      </MainContainer>
    </Root>
  )
}

export default DocumentsScreen

const MainContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `16px` : '24px')};
`
const SegmentedContainer = styled(View)`
  ${(props) => (props.theme.breakpoint === 'phone' ? `margin-bottom: 16px;` : `margin: 24px 0px;`)}
`
const DocumentList = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  ${(props) => props.theme.breakpoint !== 'phone' && `flex-wrap: wrap;`}
`
const SelectCard = styled(View)`
  ${(props) => (props.theme.breakpoint === 'phone' ? 'margin-bottom: 16px;' : 'margin: 0px 16px 16px 0px;')}
  border-radius: 6px;
  ${(props) => props.theme.breakpoint === 'phone' && props.theme.constants.shadow};
`
